import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { api } from "@/lib/api";
import Home from "@/pages/Home";
import SectionPage from "@/pages/SectionPage";
import PostPage from "@/pages/PostPage";
import SeriesPage from "@/pages/SeriesPage";
import Colophon from "@/pages/Colophon";
import CatPicker from "@/pages/CatPicker";
import PreviewPage from "@/pages/PreviewPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const LoadingVeil = () => (
  <div className="min-h-screen bg-[#070709] flex items-center justify-center" data-testid="loading-veil">
    <p className="font-mono text-xs tracking-[0.4em] uppercase text-[#78716c] flicker">Unsealing the vault…</p>
  </div>
);

const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    api.post("/auth/google-session", { session_id: sessionId })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true, state: { user: data } });
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate, setUser]);

  return <LoadingVeil />;
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (location.state?.user) return children;
  if (user === null) return <LoadingVeil />;
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Detect session_id synchronously during render to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/s/:slug" element={<SectionPage />} />
      <Route path="/post/:slug" element={<PostPage />} />
      <Route path="/series/:name" element={<SeriesPage />} />
      <Route path="/colophon" element={<Colophon />} />
      <Route path="/cat-picker" element={<CatPicker />} />
      <Route path="/preview/:token" element={<PreviewPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let frame;
    const raf = (t) => { lenis.raf(t); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);

  return (
    <div className="App">
      <div className="vignette" />
      <div className="grain-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <SiteProvider>
            <Toaster theme="dark" position="bottom-right" />
            <AppRouter />
          </SiteProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
